import React, { useCallback, useState } from "react";

import { colorsSelector } from "@redux/colorPalette/selectors";
import { userTypeSelector } from "@redux/user/selectors";
import throttle from "lodash/throttle";
import { useSelector } from "react-redux";

import { MANUAL_TIME_UPDATE_EVENT_TYPE } from "components/b2c/Mp3KbpPlayer/Mp3KbpPlayer.constants";
import {
	getPixelSizeNumberFromSizingFactor,
	getCurrentTimeInSong,
} from "components/b2c/Mp3KbpPlayer/Mp3KbpPlayer.utils";
import { SECONDS_TO_MS_RATIO } from "services/helpers/formatTime";
import { useMediaElementListener } from "services/hooks/useMediaElementListener";
import { useVideoIsPlaying } from "services/hooks/useVideoIsPlaying";
import { SCOPE } from "shared-types/constants";
import { Timer as TimerInfo } from "shared-types/lyrics";
import { colorUsage as b2cColorUsage } from "stylesheetB2c";

import {
	TimerContainer,
	ProgressMarkerContainer,
	ProgressMarker,
	SIZING_FACTORS,
	CircularProgressContainer,
	StyledTimerLogo,
} from "./Timer.style";
import {
	findCurrentTimer,
	getTimerProgressPercentage,
	shouldDisplayCurrentTimer,
} from "./Timer.utils";
import CircularProgress from "../CircularProgress";
import { B2bUserType } from "shared-types/user";
import TimerBam from "./TimerBam";
import LogoGrid from "../LogoGrid/LogoGrid";

const CURRENT_TIMER_SEARCH_MAX_FREQUENCY_IN_MS = 250;

interface Props {
	videoElement: HTMLVideoElement;
	timersInfo: TimerInfo[];
	videoAreaHeight: number;
	isPremium: boolean;
	lastManualUpdateTimeInMs: number;
	scope: SCOPE;
}

const Timer: React.FC<Props> = ({
	videoElement,
	timersInfo,
	videoAreaHeight,
	isPremium,
	lastManualUpdateTimeInMs,
	scope,
}) => {
	const [isVisible, setIsVisible] = useState(false);
	const [currentTimer, setCurrentTimer] = useState<TimerInfo | null>(null);
	const [rerender, setRerender] = useState(0);
	const isPlaying = useVideoIsPlaying(videoElement);

	const onTimeUpdate = useCallback(
		throttle((currentTime: number) => {
			setCurrentTimer(findCurrentTimer(timersInfo, currentTime));
		}, CURRENT_TIMER_SEARCH_MAX_FREQUENCY_IN_MS),
		[timersInfo, setCurrentTimer],
	);
	useMediaElementListener(videoElement, "timeupdate", () => {
		onTimeUpdate(getCurrentTimeInSong(videoElement, isPremium));
		setIsVisible(
			shouldDisplayCurrentTimer(
				currentTimer,
				getCurrentTimeInSong(videoElement, isPremium) * SECONDS_TO_MS_RATIO,
			),
		);
	});

	useMediaElementListener(videoElement, MANUAL_TIME_UPDATE_EVENT_TYPE, () => {
		setRerender(rerender + 1);
	});

	const b2bColors = useSelector(colorsSelector);
	const userType = useSelector(userTypeSelector);

	const markerSize = getPixelSizeNumberFromSizingFactor(
		videoAreaHeight,
		SIZING_FACTORS.markerSize,
	);

	const timerDurationInMs = currentTimer
		? currentTimer.timerEnd - currentTimer.timerStart
		: 0;

	const delayInMs = currentTimer
		? currentTimer.timerStart -
			Math.max(currentTimer.timerStart, lastManualUpdateTimeInMs)
		: 0;

	const colorUsage = scope === SCOPE.B2B ? b2bColors : b2cColorUsage;

	const progress = currentTimer
		? getTimerProgressPercentage(
				getCurrentTimeInSong(videoElement, isPremium) * SECONDS_TO_MS_RATIO,
				currentTimer.timerStart,
				currentTimer.timerEnd,
			)
		: -1;

	return (
		<>
			{userType === B2bUserType.BAM && <LogoGrid isVisible={isVisible} />}
			<TimerContainer
				isVisible={isVisible}
				isBam={userType === B2bUserType.BAM}
				key={rerender}
			>
				{progress >= 0 &&
					progress < 100 &&
					(userType === B2bUserType.BAM ? (
						<TimerBam
							durationMs={timerDurationInMs}
							progress={progress}
							isPlaying={isPlaying}
						/>
					) : (
						<>
							<CircularProgressContainer>
								<CircularProgress
									timerDurationInMs={timerDurationInMs}
									isPlaying={isPlaying}
									delayInMilliseconds={delayInMs}
									mp3KbpPlayerColors={colorUsage.mp3KbpPlayer}
								/>
							</CircularProgressContainer>
							<ProgressMarkerContainer
								timerDurationInMs={timerDurationInMs}
								isPlaying={isPlaying}
								markerSize={markerSize}
								delayInMilliseconds={delayInMs}
							>
								<ProgressMarker
									markerSize={markerSize}
									mp3KbpPlayerColors={colorUsage.mp3KbpPlayer}
								/>
							</ProgressMarkerContainer>
							<StyledTimerLogo {...{ scope, userType }} />
						</>
					))}
			</TimerContainer>
		</>
	);
};

export default Timer;
